import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, Loader2 } from "lucide-react";
import { useEffect, useRef } from "react";
import { activateOrganization } from "@/api/billing";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useOrganizationStore } from "@/store/organization";
import { ApiRequestError } from "@/utils/fetch";
import { applyActivatedOrganization } from "../lib/apply-activated-organization";
import {
  isPaymentMethodRequiredError,
  paymentErrorMessage,
} from "../lib/payment-errors";

/**
 * Resume state for "unexpired invoice-default card, org still pending": activates on
 * mount (the endpoint is idempotent, so a refresh here is harmless). An exact
 * `payment_method_required` response hands control back to the payment step.
 * Queued requests and results are ignored after the user selects a different
 * organization.
 *
 * @param onPaymentRequired - Receives recovery guidance when activation needs a new card.
 */
export const ActivatingStep = ({
  onPaymentRequired,
}: {
  onPaymentRequired: (message: string) => void;
}) => {
  const organizationId = useOrganizationStore((s) => s.organization?.id);
  const queryClient = useQueryClient();
  const startedRef = useRef(false);
  const isCurrentOrganization = () =>
    useOrganizationStore.getState().organization?.id === organizationId;

  const activateMutation = useMutation({
    retry: false,
    mutationFn: () => {
      if (!organizationId) {
        throw new Error("Organization is unavailable for activation");
      }
      if (!isCurrentOrganization()) {
        throw new Error("Organization selection changed before activation");
      }
      return activateOrganization(organizationId);
    },
    onSuccess: (activated) => {
      if (!organizationId || !isCurrentOrganization()) return;
      return applyActivatedOrganization(queryClient, organizationId, activated);
    },
    onError: (err) => {
      if (isCurrentOrganization() && isPaymentMethodRequiredError(err)) {
        onPaymentRequired(paymentErrorMessage(err));
      }
    },
  });

  useEffect(() => {
    if (!startedRef.current && organizationId) {
      startedRef.current = true;
      activateMutation.mutate();
    }
  }, [organizationId, activateMutation]);

  const failedForOtherReason =
    !organizationId ||
    (activateMutation.isError &&
      !isPaymentMethodRequiredError(activateMutation.error));
  const failureMessage =
    activateMutation.error instanceof ApiRequestError &&
    activateMutation.error.status === 503
      ? paymentErrorMessage(activateMutation.error)
      : "Activation failed. Please try again.";

  return (
    <>
      <div>
        <h2 className="text-3xl font-bold tracking-tight">
          Activating your account
        </h2>
        <p className="mt-3 text-muted-foreground">
          Your card is saved — finishing setup.
        </p>
      </div>

      {failedForOtherReason ? (
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription className="flex items-center justify-between gap-4">
            <span>
              {organizationId
                ? failureMessage
                : "We couldn't find your organization. Refresh the page and try again."}
            </span>
            {organizationId && (
              <Button
                variant="outline"
                size="sm"
                disabled={activateMutation.isPending}
                onClick={() => activateMutation.mutate()}
              >
                Retry
              </Button>
            )}
          </AlertDescription>
        </Alert>
      ) : (
        <div className="flex justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      )}
    </>
  );
};
